"use client";

import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "@/store/store";
import { setWalletData } from "@/store/slices/wallet.slice";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Wallet,
  Plus,
  Minus,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Eye,
  EyeOff,
} from "lucide-react";
import { WalletAddMoneyModal } from "@/components/modals/WalletAddMoneyModal";
import { WalletWithdrawModal } from "@/components/modals/WalletWithdrawModal";
import { formatCurrency } from "@/utils/currency";
import { NavigationTabs } from "@/components/common/navigationTabs";
import { DataView } from "@/components/common/DataView";
import { TableContainer } from "@/components/common/TableContainer";
import { columns } from "@/components/wallet/columns";
import {
  getBalance,
  getTransactions,
  addFunds,
  withdrawFunds,
  WalletTransaction,
} from "@/services/walletService";
import { toast } from "sonner";

// Map API response to component's expected format
interface Transaction {
  id: string;
  type: "credit" | "debit";
  amount: number;
  description: string;
  status: "completed" | "pending" | "failed";
  date: string;
  reference?: string;
}

interface WalletBalance {
  balance: number;
  pendingBalance: number;
}

export default function WalletPage() {
  const [balance, setBalance] = useState<WalletBalance>({
    balance: 0,
    pendingBalance: 0,
  });
  const [activeTab, setActiveTab] = useState<"all" | "incoming" | "outgoing">(
    "all",
  );
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 10;

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [totalTransactions, setTotalTransactions] = useState(0);

  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [balanceVisible, setBalanceVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedInitialData = useRef(false);
  const dispatch = useDispatch<AppDispatch>();
  const walletState = useSelector((state: RootState) => state.wallet);

  // Load wallet balance
  const loadBalance = async () => {
    try {
      const response = await getBalance();
      if (response.data) {
        setBalance(response.data as WalletBalance);
      }
    } catch (error: any) {
      console.error("Failed to load balance:", error);
      setError(error?.message || "Failed to load balance");
      toast.error("Failed to load wallet balance");
    }
  };

  // Load transactions with pagination
  const loadTransactions = async (page: number = 1) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getTransactions(page, pageSize);
      
      if (response.data) {
        // Map API response to component format
        const apiTransactions = response.data as WalletTransaction[];
        const mappedTransactions: Transaction[] = apiTransactions.map((t) => ({
          id: t.id,
          type: t.type,
          amount: t.amount,
          description: t.description,
          status: t.status,
          date: t.createdAt,
          reference: t.reference,
        }));
        setTransactions(mappedTransactions);
        
        // Update total if pagination info is available
        const total = (response as any).pagination?.total || 0;
        setTotalTransactions(total);

        // Persist fetched data into the wallet slice so other mounts can reuse it
        try {
          dispatch(
            setWalletData({
              balance,
              transactions: mappedTransactions,
              total,
            }),
          );
        } catch (e) {
          // ignore dispatch errors
        }
      }
    } catch (error: any) {
      console.error("Failed to load transactions:", error);
      setError(error?.message || "Failed to load transactions");
      toast.error("Failed to load transactions");
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on mount and when page changes
  useEffect(() => {
    // If wallet slice already has data, use it and skip initial fetch
    if (walletState?.loaded) {
      hasLoadedInitialData.current = true;
      if (walletState.balance) setBalance(walletState.balance as WalletBalance);
      if (walletState.transactions) setTransactions(walletState.transactions as Transaction[]);
      if (walletState.total) setTotalTransactions(walletState.total as number);
      return;
    }

    // Initial mount
    if (!hasLoadedInitialData.current && pageIndex === 0) {
      hasLoadedInitialData.current = true;
      loadBalance();
      loadTransactions(1);
    } else if (hasLoadedInitialData.current && pageIndex > 0) {
      // Pagination - only fetch transactions for next page
      loadTransactions(pageIndex + 1);
    }
  }, [pageIndex, walletState?.loaded]);

  const creditTransactions = transactions.filter((t) => t.type === "credit");
  const debitTransactions = transactions.filter((t) => t.type === "debit");

  const getFilteredTransactions = () => {
    let filtered;
    switch (activeTab) {
      case "incoming":
        filtered = creditTransactions;
        break;
      case "outgoing":
        filtered = debitTransactions;
        break;
      default:
        filtered = transactions;
    }
    return filtered;
  };

  const filteredTransactions = getFilteredTransactions();
  const totalPages = Math.ceil(
    (totalTransactions || filteredTransactions.length) / pageSize,
  );

  // Create pagination object for DataView
  const pagination = {
    total: totalTransactions || filteredTransactions.length,
    page: pageIndex + 1,
    pageSize: pageSize,
    totalPages: totalPages,
  };

  const handleTabChange = (tab: "all" | "incoming" | "outgoing") => {
    setActiveTab(tab);
    setPageIndex(0);
  };

  const handlePageChange = (newPageIndex: number) => {
    setPageIndex(newPageIndex);
  };

  // Get current page data
  const currentPageData = filteredTransactions.slice(
    pageIndex * pageSize,
    (pageIndex + 1) * pageSize,
  );

  const handleAddMoney = async (amount: number) => {
    try {
      // Convert rupees to paise (multiply by 100) since balance is stored in paise
      const amountInPaise = amount * 100;
      await addFunds(amountInPaise);
      
      toast.success("Funds added successfully");
      
      // Reload balance and transactions
      await loadBalance();
      await loadTransactions(pageIndex + 1);
    } catch (error: any) {
      console.error("Failed to add funds:", error);
      toast.error(error?.message || "Failed to add funds");
    }
  };

  const handleWithdraw = async (amount: number) => {
    try {
      // Convert rupees to paise (multiply by 100) since balance is stored in paise
      const amountInPaise = amount * 100;
      await withdrawFunds(amountInPaise);
      
      toast.success("Withdrawal request submitted successfully");
      
      // Reload balance and transactions
      await loadBalance();
      await loadTransactions(pageIndex + 1);
    } catch (error: any) {
      console.error("Failed to withdraw:", error);
      toast.error(error?.message || "Failed to process withdrawal");
    }
  };

  const refreshBalance = async () => {
    setIsLoading(true);
    try {
      await Promise.all([loadBalance(), loadTransactions(pageIndex + 1)]);
      toast.success("Wallet data refreshed");
    } catch (error) {
      // Error already handled in load functions
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Glassmorphism */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-100 via-primary/8 to-blue-50 dark:from-primary/20 dark:via-primary/10 dark:to-background border border-primary/20 dark:border-primary/30 shadow-xl">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.08] dark:opacity-[0.05]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%235659ff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        {/* Floating Elements */}
        <div className="absolute top-4 right-8 w-32 h-32 bg-gradient-to-br from-primary/30 to-white/90 dark:from-primary/10 dark:to-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-4 left-8 w-28 h-28 bg-gradient-to-br from-white/95 to-primary/20 dark:from-primary/8 dark:to-primary/4 rounded-full blur-3xl" />

        <div className="relative z-10 flex items-center justify-between p-6">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-foreground flex items-center gap-3">
              <div className="p-2 bg-white/90 dark:bg-card/50 backdrop-blur-sm rounded-xl border border-primary/20">
                <Wallet className="h-8 w-8 text-primary" />
              </div>
              Wallet
            </h1>
            <p className="text-slate-600 dark:text-muted-foreground">
              Manage your wallet balance and transactions
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={refreshBalance}
              disabled={isLoading}
              className="bg-white/90 dark:bg-card/50 backdrop-blur-sm border-primary/20 hover:bg-white hover:border-primary/30 dark:hover:bg-card/70"
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Balance Cards with Glassmorphism */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Main Balance - Enhanced Glassmorphism */}
        <Card className="relative overflow-hidden bg-gradient-to-br from-purple-100 via-primary/8 to-blue-50 dark:from-primary/20 dark:via-primary/10 dark:to-background border-primary/20 dark:border-primary/30 shadow-xl">
          {/* Gradient Layers */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-primary/10 dark:opacity-0" />
          <div className="absolute inset-0 bg-gradient-to-bl from-primary/15 via-transparent to-purple-100/50 dark:opacity-0" />

          {/* Floating Elements */}
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary/30 to-white/90 dark:from-primary/10 dark:to-primary/5 rounded-full blur-2xl" />
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-br from-white/95 to-primary/20 dark:from-primary/8 dark:to-primary/4 rounded-full blur-2xl" />

          <CardHeader className="pb-2 relative z-10">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-muted-foreground">
                Available Balance
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setBalanceVisible(!balanceVisible)}
                className="h-8 w-8 p-0 hover:bg-white/50 dark:hover:bg-primary/10 backdrop-blur-sm"
              >
                {balanceVisible ? (
                  <Eye className="h-4 w-4" />
                ) : (
                  <EyeOff className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0 relative z-10">
            <div className="space-y-3">
              <div className="text-3xl font-bold text-slate-900 dark:text-foreground">
                {balanceVisible ? formatCurrency(balance.balance) : "₹ •••••"}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => setShowAddMoneyModal(true)}
                  className="flex-1 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground shadow-lg backdrop-blur-sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Money
                </Button>
                <Button
                  onClick={() => setShowWithdrawModal(true)}
                  variant="outline"
                  className="flex-1 bg-white/90 dark:bg-card/50 backdrop-blur-sm border-primary/20 hover:bg-white hover:border-primary/30 dark:hover:bg-card/70"
                >
                  <Minus className="h-4 w-4 mr-2" />
                  Withdraw
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Balance with Glassmorphism */}
        <Card className="relative overflow-hidden bg-gradient-to-br from-yellow-100 via-yellow-50/50 to-orange-50 dark:from-yellow-500/10 dark:via-yellow-500/5 dark:to-orange-500/5 border-yellow-200 dark:border-yellow-500/30 shadow-xl">
          {/* Gradient Layers */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-yellow-100/30 dark:opacity-0" />

          {/* Floating Elements */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-200/60 to-white dark:from-yellow-500/10 dark:to-yellow-500/5 rounded-full blur-2xl" />

          <CardHeader className="pb-2 relative z-10">
            <CardTitle className="text-sm font-medium text-slate-600 dark:text-muted-foreground flex items-center gap-2">
              <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
              Pending Balance
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 relative z-10">
            <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
              {formatCurrency(balance.pendingBalance)}
            </div>
            <p className="text-sm text-slate-600 dark:text-muted-foreground mt-1">
              Being processed
            </p>
          </CardContent>
        </Card>

        {/* Quick Stats with Glassmorphism */}
        <Card className="relative overflow-hidden bg-gradient-to-br from-green-100 via-green-50/50 to-emerald-50 dark:from-green-500/10 dark:via-green-500/5 dark:to-emerald-500/5 border-green-200 dark:border-green-500/30 shadow-xl">
          {/* Gradient Layers */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-green-100/30 dark:opacity-0" />

          {/* Floating Elements */}
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-green-200/60 to-white dark:from-green-500/10 dark:to-green-500/5 rounded-full blur-2xl" />

          <CardHeader className="pb-2 relative z-10">
            <CardTitle className="text-sm font-medium text-slate-600 dark:text-muted-foreground">
              This Month
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 relative z-10">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-slate-600 dark:text-muted-foreground">
                    Received
                  </span>
                </div>
                <span className="font-semibold text-green-600">
                  {formatCurrency(0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-slate-600 dark:text-muted-foreground">
                    Spent
                  </span>
                </div>
                <span className="font-semibold text-red-600">
                  {formatCurrency(0)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions */}
      <div className="space-y-4">
        <TableContainer
          navigationTabs={
            <NavigationTabs
              hasBackButton={false}
              tabs={[
                {
                  label: "All Transactions",
                  onClick: () => handleTabChange("all"),
                  isActive: activeTab === "all",
                },
                {
                  label: "Incoming",
                  onClick: () => handleTabChange("incoming"),
                  isActive: activeTab === "incoming",
                },
                {
                  label: "Outgoing",
                  onClick: () => handleTabChange("outgoing"),
                  isActive: activeTab === "outgoing",
                },
              ]}
            />
          }
        >
          <div className="bg-secondary-background p-4 border rounded-[16px]">
            <DataView
              columns={columns}
              data={currentPageData}
              pagination={pagination}
              pageIndex={pageIndex}
              onPageChange={handlePageChange}
              loading={isLoading}
              error={error}
              title="Transaction History"
              description={`${activeTab === "all" ? "All" : activeTab === "incoming" ? "Incoming" : "Outgoing"} transactions for your wallet`}
              emptyStateType="wallet"
              enableColumnResizing={true}
              enableRowSelection={false}
              cardWrapper={false}
            />
          </div>
        </TableContainer>
      </div>

      {/* Modals */}
      <WalletAddMoneyModal
        isOpen={showAddMoneyModal}
        onClose={() => setShowAddMoneyModal(false)}
        onAddMoney={handleAddMoney}
      />

      <WalletWithdrawModal
        isOpen={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        onWithdraw={handleWithdraw}
        availableBalance={balance.balance}
      />
    </div>
  );
}
